import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { API } from "@/App";
import axios from "axios";
import { Building2, MapPin, Calendar, FileText, Clock, CheckCircle2 } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

export default function TrackingPage() {
  const { linkId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrackingData();
  }, [linkId]);

  const fetchTrackingData = async () => {
    try {
      const response = await axios.get(`${API}/track/${linkId}`);
      setData(response.data);
    } catch (err) {
      setError(extractErrorMessage(err, "Link inválido ou expirado"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-heading font-bold text-slate-900 mb-2">Link Inválido</h2>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { project, progress, recent_logs } = data;

  const statusLabels = {
    backlog: "Backlog",
    planned: "Planejado",
    "in-progress": "Em Progresso",
    "waiting-materials": "Aguard. Materiais",
    completed: "Concluído"
  };

  const completedCount = progress.status_breakdown?.completed || 0;
  const totalOrders = progress.total_orders || 1;
  const progressPercent = Math.round((completedCount / totalOrders) * 100);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tracking-page">
      {/* Header */}
      <header className="bg-luvcorp-navy text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <img src="/brand/obrapadrao-logo.jpeg" alt="Obra Padrão" className="h-10 w-10 rounded object-cover" />
            <div>
              <h1 className="font-heading font-bold text-lg">
                Obra <span className="text-luvcorp-gold">Padrão</span>
              </h1>
              <p className="text-[10px] text-white/60 tracking-wider">SISTEMA DE GESTÃO DE OBRAS</p>
            </div>
          </div>
          <h2 className="text-2xl font-heading font-bold">{project.name}</h2>
          <p className="flex items-center gap-1 text-white/80 text-sm mt-1">
            <MapPin className="h-4 w-4" />
            {project.location}
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Progress Card */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-luvcorp-navy flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-luvcorp-gold" />
              Progresso Geral
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Tarefas concluídas</span>
                <span className="font-bold text-luvcorp-navy">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(progress.status_breakdown || {}).map(([status, count]) => (
                <div key={status} className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-luvcorp-navy">{count}</p>
                  <p className="text-xs text-slate-500">{statusLabels[status] || status}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Timeline Card */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading text-luvcorp-navy flex items-center gap-2">
                <Calendar className="h-5 w-5 text-luvcorp-gold" />
                Cronograma
              </CardTitle>
              <Badge className={project.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>
                {project.status === "active" ? "Em Andamento" : project.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Data de Início</p>
                <p className="font-semibold text-luvcorp-navy">
                  {new Date(project.start_date).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Previsão de Término</p>
                <p className="font-semibold text-luvcorp-navy">
                  {new Date(project.estimated_end_date).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading text-luvcorp-navy flex items-center gap-2">
              <FileText className="h-5 w-5 text-luvcorp-gold" />
              Atividades Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_logs && recent_logs.length > 0 ? (
              <div className="space-y-4">
                {recent_logs.map((log, index) => (
                  <div key={index} className="flex gap-4 pb-4 border-b last:border-0 last:pb-0">
                    <div className="h-10 w-10 bg-luvcorp-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-luvcorp-navy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700">{log.description}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{log.created_by_name || "Equipe"}</span>
                        <span>•</span>
                        <span>{new Date(log.created_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                        {log.photos?.length > 0 && (
                          <>
                            <span>•</span>
                            <span>{log.photos.length} fotos</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Badge variant={log.status === "approved" ? "default" : "secondary"} className="flex-shrink-0">
                      {log.status === "approved" ? "Aprovado" : "Pendente"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-8">
                Nenhuma atividade registrada ainda
              </p>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-xs text-slate-400">
            © 2026 Obra Padrão · Sistema de Gestão de Obras. Este é um link de acompanhamento temporário.
          </p>
        </div>
      </main>
    </div>
  );
}
